// HRP-ordered correlation heatmap. Assets are ordered by the SciPy
// hierarchical-clustering leaf order, so correlated blocks sit next to each
// other and hidden structural regimes become visible.

import React from 'react';

interface Props {
    matrix: Record<string, Record<string, number>>;
    order: string[];
}

// Diverging scale: rose for positive correlation (redundancy), emerald for
// negative correlation (diversifier). Inline RGBA keeps the scale continuous.
const cellStyle = (val: number): React.CSSProperties => {
    const alpha = Math.min(Math.abs(val), 1) * 0.85 + 0.08;
    const rgb = val >= 0 ? '244, 63, 94' : '16, 185, 129';
    return {
        backgroundColor: `rgba(${rgb}, ${alpha.toFixed(3)})`,
        color: Math.abs(val) > 0.55 ? '#ffffff' : '#0f172a',
    };
};

export const CorrelationHeatmap: React.FC<Props> = ({ matrix, order }) => (
    <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
        <h3 className="text-lg font-bold text-slate-800">Hierarchical Risk Parity (HRP) Correlation Matrix</h3>
        <p className="text-xs text-slate-500 font-medium mt-1 mb-4">
            Assets ordered by structural similarity (hierarchical clustering), not alphabetically.
        </p>
        <div className="overflow-x-auto pb-2">
            <div
                className="grid gap-1 min-w-max"
                style={{ gridTemplateColumns: `auto repeat(${order.length}, minmax(3.25rem, 1fr))` }}
            >
                <div />
                {order.map((t) => (
                    <div key={`h-${t}`} className="text-center font-semibold text-slate-500 text-[11px] py-1 truncate" title={t}>
                        {t}
                    </div>
                ))}
                {order.map((row) => (
                    <React.Fragment key={row}>
                        <div className="flex items-center justify-end pr-3 font-semibold text-slate-500 text-[11px]">
                            {row}
                        </div>
                        {order.map((col) => {
                            const val = matrix[row]?.[col] ?? 0;
                            const isDiag = row === col;
                            return (
                                <div
                                    key={`${row}-${col}`}
                                    title={`${row} / ${col}: ${val.toFixed(2)}`}
                                    style={isDiag ? undefined : cellStyle(val)}
                                    className={`flex items-center justify-center text-[11px] font-semibold rounded-md h-10 transition-transform hover:scale-110 ${
                                        isDiag ? 'bg-slate-100 text-slate-400' : ''
                                    }`}
                                >
                                    {isDiag ? '-' : val.toFixed(2)}
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </div>
        <div className="flex items-center gap-3 mt-3 text-[11px] font-medium text-slate-500">
            <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(16, 185, 129, 0.8)' }}></span>
                Negative (diversifier)
            </span>
            <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-slate-100 border border-slate-200"></span>
                Uncorrelated
            </span>
            <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(244, 63, 94, 0.8)' }}></span>
                Positive (redundant)
            </span>
        </div>
    </div>
);
